/**
 * Donut — a clean multi-segment ring chart (DS core). Solid segments separated by
 * thin gaps that show the background behind it (place it on a white panel for white
 * dividers), plus a free center slot. Colours are passed per segment (token vars),
 * so it stays content/brand-agnostic.
 */
import type { ReactNode } from "react";

export type DonutSegment = { value: number; color: string };

export function Donut({
  segments,
  size = 124,
  stroke = 22,
  gap = 7,
  rim = 3,
  rimColor = "var(--surface)",
  center,
  className = "",
}: {
  segments: DonutSegment[];
  size?: number;
  stroke?: number;
  /** Divider width between segments, in px of arc (shows the rim colour). */
  gap?: number;
  /** White rim drawn around the ring's edges + through the gaps. 0 = none. */
  rim?: number;
  rimColor?: string;
  center?: ReactNode;
  className?: string;
}) {
  // The rim sits a touch outside the coloured band, so the band fits inside it.
  const r = (size - stroke - rim * 2) / 2;
  const C = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;

  let cursor = 0;
  const arcs = segments.map((s, i) => {
    const len = (s.value / total) * C;
    const dash = Math.max(len - gap, 1);
    const offset = cursor;
    cursor += len;
    return (
      <circle
        key={i}
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={s.color}
        strokeWidth={stroke}
        strokeLinecap="butt"
        strokeDasharray={`${dash} ${C - dash}`}
        strokeDashoffset={-offset}
      />
    );
  });

  return (
    <div className={`relative shrink-0 ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        {/* white rim + dividers: a wider ring of rimColour under the segments */}
        {rim > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={rimColor} strokeWidth={stroke + rim * 2} />}
        {arcs}
      </svg>
      {center && <div className="absolute inset-0 grid place-items-center">{center}</div>}
    </div>
  );
}
